'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { type ReactNode } from 'react';
import { useGardenPlannerStore } from '@shared/store/useGardenPlannerStore';
import { GardenPlannerProvider } from '@modules/garden-planner/context/garden_planner_context';
import { GardenSectionContainer } from '@modules/garden-planner/containers/garden_section_container';
import { SidebarContainer } from '@modules/garden-planner/containers/sidebar/sidebar_container';
import type { GardenPlannerViews } from '@modules/garden-planner/garden_planner_views';
import type { AlertConfig, ConfirmRequest } from '@modules/garden-planner/models';

export type { AlertConfig };

export interface GardenPlannerProps {
  isLoading: boolean;
  alerts: AlertConfig[];
  onDismissAlert: (id: string) => void;
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  sidebar: ReactNode;
  main: ReactNode;
}

interface GardenPlannerContainerProps {
  views: GardenPlannerViews;
  gardenId: Id<'gardens'>;
  alerts: AlertConfig[];
  onDismissAlert: (id: string) => void;
  onNavigateBack: () => void;
  onConfirm: (request: ConfirmRequest) => void;
  onShareCopy: () => Promise<void>;
}

export function GardenPlannerContainer({
  views,
  gardenId,
  alerts,
  onDismissAlert,
  onNavigateBack,
  onConfirm,
  onShareCopy,
}: GardenPlannerContainerProps) {
  const garden = useQuery(api.gardens.queries.getGardenById, { gardenId });
  const plants = useQuery(api.plants.queries.getAllPlants, {});

  const isSidebarOpen = useGardenPlannerStore((state) => state.isSidebarOpen);
  const setIsSidebarOpen = useGardenPlannerStore((state) => state.setIsSidebarOpen);

  const isLoading = garden === undefined || plants === undefined;
  const { GardenPlanner: GardenPlannerView } = views;

  if (isLoading || garden === null) {
    return (
      <GardenPlannerView
        isLoading={isLoading}
        alerts={alerts}
        onDismissAlert={onDismissAlert}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        sidebar={null}
        main={null}
      />
    );
  }

  return (
    <GardenPlannerProvider
      garden={garden}
      plants={plants}
      onConfirm={onConfirm}
      onShareCopy={onShareCopy}>
      <GardenPlannerView
        isLoading={false}
        alerts={alerts}
        onDismissAlert={onDismissAlert}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        sidebar={<SidebarContainer views={views} onNavigateBack={onNavigateBack} />}
        main={<GardenSectionContainer views={views} />}
      />
    </GardenPlannerProvider>
  );
}
